import { useState } from 'react';
import { MapPin, Phone, Mail, Clock, Send, CheckCircle, MessageCircle } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';

const Contact = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Send via WhatsApp
    const phone = '5531991640648';
    const message = `${t('whatsapp.newMessage')}%0A%0A${t('whatsapp.name')} ${formData.name}%0A${t('contact.email')}: ${formData.email}%0A${t('whatsapp.phone')} ${formData.phone}%0A%0A*${t('contact.message')}:*%0A${formData.message}`;
    window.open(`https://wa.me/${phone}?text=${message}`, '_blank');
    
    setSubmitted(true);
    setTimeout(() => {
      setSubmitted(false);
      setFormData({ name: '', email: '', phone: '', message: '' });
    }, 3000);
  };

  return (
    <div className="min-h-screen bg-[#f6f6f8] pt-28 pb-20">
      {/* Hero */}
      <div
        className="relative h-[400px] mb-20 parallax"
        style={{
          backgroundImage: 'url("https://lh3.googleusercontent.com/aida-public/AB6AXuCWmfF8h0POfz1C5Lr1PCGinH2U48LQiGdPcEFMRTzI0YATRWreGJAEYChsW266PNh1XFVFXWwdK8A8G6kucCE_Oi1IBP86g8OFvX6slyw7YkGcl8TkTg3o7d7AhHfxOfCWou3fN455xa8-IKscUlfZ1t6EEmmknWg88gbPWwgvNovakJ3ksNWIpV75oh_-DwM4y4bB_XG2SiIjcv1w5T8__Le0XM-sROjN1D3ycoTxIdKagKlYOUSlJl_VpQI1J0M31T6ZENMLKTs")'
        }}
        data-testid="contact-hero"
      >
        <div className="absolute inset-0 bg-[#101B2D]/60"></div>
        <div className="relative z-10 h-full flex items-center justify-center">
          <div className="text-center animate-fade-in">
            <h1 className="text-5xl md:text-6xl font-extrabold text-white mb-4">{t('contact.title')}</h1>
            <p className="text-xl text-gray-200">{t('contact.subtitle')}</p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Info */}
          <div className="space-y-8 animate-slide-in-left" data-testid="contact-info">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-6">{t('contact.getInTouch')}</h2>
              <p className="text-gray-600 text-lg leading-relaxed">
                {t('contact.getInTouchDesc')}
              </p>
            </div>

            {/* Contact Cards */}
            <div className="space-y-4">
              <div className="bg-white p-6 rounded-xl shadow-lg flex items-start gap-4 hover-scale">
                <div className="w-12 h-12 bg-[#C5A059]/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6 text-[#C5A059]" />
                </div>
                <div>
                  <h3 className="font-bold text-slate-900 mb-1">{t('contact.address')}</h3>
                  <p className="text-gray-600 text-sm">
                    Largo de Coimbra, 38<br />
                    Centro Histórico<br />
                    Ouro Preto, MG - 35400-000
                  </p>
                </div>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-lg flex items-start gap-4 hover-scale">
                <div className="w-12 h-12 bg-[#C5A059]/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <Phone className="w-6 h-6 text-[#C5A059]" />
                </div>
                <div>
                  <h3 className="font-bold text-slate-900 mb-1">{t('contact.phone')}</h3>
                  <a
                    href="tel:+553135527700"
                    className="text-gray-600 text-sm hover:text-[#C5A059] transition-colors"
                    data-testid="contact-phone-link"
                  >
                    +55 31 3552-7700
                  </a>
                </div>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-lg flex items-start gap-4 hover-scale">
                <div className="w-12 h-12 bg-[#C5A059]/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <MessageCircle className="w-6 h-6 text-[#C5A059]" />
                </div>
                <div>
                  <h3 className="font-bold text-slate-900 mb-1">{t('contact.whatsappContact')}</h3>
                  <a
                    href="https://wa.me/5531991640648"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-600 text-sm hover:text-[#C5A059] transition-colors"
                    data-testid="contact-whatsapp-link"
                  >
                    +55 31 99164-0648
                  </a>
                </div>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-lg flex items-start gap-4 hover-scale">
                <div className="w-12 h-12 bg-[#C5A059]/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <Mail className="w-6 h-6 text-[#C5A059]" />
                </div>
                <div>
                  <h3 className="font-bold text-slate-900 mb-1">{t('contact.email')}</h3>
                  <a
                    href="mailto:reservas@mondego.com.br"
                    className="text-gray-600 text-sm hover:text-[#C5A059] transition-colors"
                    data-testid="contact-email-link"
                  >
                    reservas@mondego.com.br
                  </a>
                </div>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-lg flex items-start gap-4 hover-scale">
                <div className="w-12 h-12 bg-[#C5A059]/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <Clock className="w-6 h-6 text-[#C5A059]" />
                </div>
                <div>
                  <h3 className="font-bold text-slate-900 mb-1">{t('contact.serviceHours')}</h3>
                  <p className="text-gray-600 text-sm">
                    {t('contact.mondayToFriday')}: 09:00 - 18:00<br />
                    {t('contact.saturday')}: 09:00 - 15:00<br />
                    {t('contact.sundaysHolidays')}: {t('contact.closed')}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="animate-slide-in-right" data-testid="contact-form-section">
            <div className="bg-white p-8 rounded-2xl shadow-xl">
              <h2 className="text-3xl font-bold text-slate-900 mb-6">{t('contact.sendMessage')}</h2>
              
              {submitted ? (
                <div className="text-center py-12" data-testid="form-success-message">
                  <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">{t('contact.messageSent')}</h3>
                  <p className="text-gray-600">{t('contact.willReturnSoon')}</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                      {t('contact.fullName')} *
                    </label>
                    <input
                      type="text"
                      id="name"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C5A059] focus:border-transparent"
                      placeholder={t('contact.yourName')}
                      data-testid="contact-name-input"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                      {t('contact.email')} *
                    </label>
                    <input
                      type="email"
                      id="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C5A059] focus:border-transparent"
                      placeholder={t('contact.yourEmail')}
                      data-testid="contact-email-input"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                      {t('contact.phone')}
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C5A059] focus:border-transparent"
                      placeholder="(31) 99999-9999"
                      data-testid="contact-phone-input"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                      {t('contact.message')} *
                    </label>
                    <textarea
                      id="message"
                      required
                      rows={6}
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C5A059] focus:border-transparent resize-none"
                      placeholder={t('contact.howCanWeHelp')}
                      data-testid="contact-message-input"
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    className="w-full btn-gold rounded-lg px-8 py-4 font-bold text-lg flex items-center justify-center gap-2"
                    data-testid="contact-submit-button"
                  >
                    <Send className="w-5 h-5" />
                    {t('contact.sendButton')}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>

        {/* Map */}
        <div className="mt-20 animate-slide-up" data-testid="contact-map">
          <h2 className="text-3xl font-bold text-slate-900 mb-6 text-center">{t('contact.ourLocation')}</h2>
          <div className="bg-white p-4 rounded-2xl shadow-xl">
            <div className="aspect-video rounded-xl overflow-hidden">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3750.8916346547567!2d-43.50566268509073!3d-20.385664486368186!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xa692d0e12cf5bf%3A0x8e63ca5c0c4e8e49!2sLargo%20de%20Coimbra%2C%20Ouro%20Preto%20-%20MG!5e0!3m2!1spt-BR!2sbr!4v1234567890123!5m2!1spt-BR!2sbr"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Localização da Pousada do Mondego"
              ></iframe>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
